import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Field,
  FieldType,
  MemberAddeditComponent,
  MemberAddeditDialogModel,
} from 'src/app/components/brokeragency/member-addedit/member-addedit.component';
import {
  GroupInterface,
  GroupMemberInterface,
} from '../../../../interfaces/groups/group.interface';
import { MarketingArchiveStatusFilter } from '../../../../interfaces/campaigns/campaign.interface';
import { GroupsService } from '../../../../services/groups/groups.service';
import { UiAlertService } from '../../../../services/ui-alert/ui-alert.service';

const typeDictionary = {
  [GroupMemberInterface.Team]: 'Teams',
  [GroupMemberInterface.Company]: 'Companies',
  [GroupMemberInterface.Brand]: 'Brands',
  [GroupMemberInterface.EmailAddress]: 'Email/SMS Addresses',
  [GroupMemberInterface.ExistingUser]: 'Users',
};

export function getTypeName(type: GroupMemberInterface): string {
  return typeDictionary[type] || 'Unknown';
}

const memberSearchPlaceholderDictionary = {
  [GroupMemberInterface.Team]: 'Search teams...',
  [GroupMemberInterface.Company]: 'Search companies...',
  [GroupMemberInterface.Brand]: 'Search brands...',
  [GroupMemberInterface.EmailAddress]: 'Search email/SMS addresses...',
  [GroupMemberInterface.ExistingUser]: 'Search users...',
};

export function getMemberSearchPlaceholder(type: GroupMemberInterface): string {
  return memberSearchPlaceholderDictionary[type] || 'Search members...';
}
@Component({
  selector: 'app-marketing-campaigns-groups',
  templateUrl: './marketing-campaigns-groups.component.html',
  styleUrls: ['./marketing-campaigns-groups.component.scss'],
})
export class MarketingCampaignsGroupsComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort!: MatSort;

  columnNames = ['name', 'description', 'status', 'memberType', 'createdAt', 'actionmenu'];
  tableDataSource = new MatTableDataSource<GroupInterface>();
  public getTypeName = getTypeName;
  groupArchiveStatusFilter: MarketingArchiveStatusFilter = 'active';
  archiveStatusOptions: Array<{ label: string; value: MarketingArchiveStatusFilter }> = [
    { label: 'All', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Archived', value: 'archived' },
  ];

  constructor(
    private groupsService: GroupsService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private router: Router,
    private uiAlertService: UiAlertService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngAfterViewInit(): void {
    this.tableDataSource.sort = this.sort;
  }

  load(): void {
    this.groupsService.getAll(this.groupArchiveStatusFilter).then((groups) => {
      this.tableDataSource.data = groups;
      this.openRouteGroupDialog(groups);
    });
  }

  onArchiveStatusFilterChange(): void {
    this.load();
  }

  getGroupTypeCount(): number {
    return new Set(this.tableDataSource.data.map(group => group.memberType)).size;
  }

  getMemberTypeCount(memberType: GroupMemberInterface | string): number {
    return this.tableDataSource.data.filter(group => group.memberType === memberType).length;
  }

  getStatusClass(status: string): string {
    return `status-${status || 'active'}`;
  }

  getStatusLabel(group: GroupInterface): string {
    return group.status || 'active';
  }

  isArchived(group: GroupInterface): boolean {
    return group.status === 'archived';
  }

  async add(): Promise<void> {
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(
        { memberType: GroupMemberInterface.Team },
        this.getGroupDialogFields(),
        'Group',
        true,
        'Add',
        true,
      ),
    });
    let dialogOpen = true;
    dialogRef.componentInstance.refreshResolvedDropdownValues();
    Promise.resolve().then(() => {
      if (dialogOpen) {
        dialogRef.componentInstance.refreshResolvedDropdownValues();
      }
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      dialogOpen = false;
      if (!result) {
        return;
      }

      try {
        await this.groupsService.createGroup(
          this.toCreatePayload(result),
          result.memberType,
        );
        this.snackBar.open('Group created.', '', { duration: 1000 });
        this.load();
      } catch (error) {
        console.error(error);
        this.snackBar.open('Error while creating a Group. Try again in a few minutes.', '', {
          duration: 2000,
        });
      }
    });
  }

  async edit(group: GroupInterface): Promise<void> {
    let groupDetails: GroupInterface;
    try {
      groupDetails = await this.groupsService.getById(group.id);
    } catch (error) {
      console.error(error);
      this.snackBar.open('Unable to load group details. Try again in a few minutes.', '', {
        duration: 2000,
      });
      this.clearRouteGroupId(group.id);
      return;
    }

    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(
        this.toGroupDialogData(groupDetails),
        this.getGroupDialogFields(),
        'Group',
        true,
        'Edit',
        true,
      ),
    });
    let dialogOpen = true;
    dialogRef.componentInstance.refreshResolvedDropdownValues();
    Promise.resolve().then(() => {
      if (dialogOpen) {
        dialogRef.componentInstance.refreshResolvedDropdownValues();
      }
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      dialogOpen = false;
      if (!result) {
        return;
      }

      try {
        await this.groupsService.updateById(group.id, this.toUpdatePayload(result));
        this.snackBar.open('Group updated.', '', { duration: 1000 });
        this.load();
      } catch (error) {
        console.error(error);
        this.snackBar.open('Error while updating a Group. Try again in a few minutes.', '', {
          duration: 2000,
        });
      }
    });
    return;
  }

  async view(group: Pick<GroupInterface, 'id'>): Promise<void> {
    let groupDetails: GroupInterface;
    try {
      groupDetails = await this.groupsService.getById(group.id);
    } catch (error) {
      console.error(error);
      this.snackBar.open('Unable to load group details. Try again in a few minutes.', '', {
        duration: 2000,
      });
      this.clearRouteGroupId(group.id);
      return;
    }

    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(
        this.toGroupDialogData(groupDetails),
        this.getGroupDialogFields(true),
        'Group',
        true,
        'View',
      ),
    });
    dialogRef.componentInstance.refreshResolvedDropdownValues();
    Promise.resolve().then(() => dialogRef.componentInstance.refreshResolvedDropdownValues());
    dialogRef.afterClosed().subscribe(() => {
      this.clearRouteGroupId(group.id);
    });
  }

  async archive(group: GroupInterface): Promise<void> {
    const ok = await this.uiAlertService.presentAlertConfirm(
      'Do you really want to archive this group?',
    );
    if (!ok) {
      return;
    }

    try {
      await this.groupsService.archiveById(group.id);
      this.snackBar.open('Group archived.', '', { duration: 2000 });
      this.load();
    } catch (error) {
      console.error(error);
      this.snackBar.open('Error while archiving this Group. Try again in a few minutes.', '', {
        duration: 2000,
      });
    }
  }

  private openRouteGroupDialog(groups: GroupInterface[]): void {
    const groupId = this.route.snapshot.queryParamMap.get('groupId');
    if (!groupId) {
      return;
    }

    const group = groups.find(item => item.id === groupId) || { id: groupId };
    this.view(group).then();
  }

  private clearRouteGroupId(groupId: string): void {
    if (this.route.snapshot.queryParamMap.get('groupId') !== groupId) {
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { groupId: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    }).then();
  }

  private getGroupDialogFields(readOnly = false): Field[] {
    return [
      {
        name: 'name',
        label: 'Name',
        fieldType: FieldType.F_STRING,
        required: true,
        readOnly,
        sectionTitle: 'Group Details',
      },
      {
        name: 'description',
        label: 'Description',
        fieldType: FieldType.F_STRING,
        required: true,
        readOnly,
      },
      {
        name: 'memberType',
        label: 'Member Type',
        fieldType: FieldType.F_DROPDOWN,
        values: Object.values(GroupMemberInterface).map(value => `${getTypeName(value)}:${value}`),
        required: true,
        readOnly,
        sectionTitle: 'Members',
      },
      {
        name: 'memberIds',
        label: 'Members',
        labelResolver: form => getTypeName(form.getRawValue().memberType as GroupMemberInterface),
        fieldType: FieldType.F_DROPDOWN,
        multiple: true,
        searchable: true,
        searchPlaceholderResolver: form => getMemberSearchPlaceholder(form.getRawValue().memberType as GroupMemberInterface),
        valuesLoader: form => this.loadMemberOptions(
          form.getRawValue().memberType as GroupMemberInterface,
          form.getRawValue().memberIds,
        ),
        valuesLoaderDependencies: ['memberType'],
        required: true,
        readOnly,
        invisible: form => form.getRawValue().memberType === GroupMemberInterface.EmailAddress,
      },
      {
        name: 'emailAddressesText',
        label: 'Email Addresses',
        fieldType: FieldType.F_STRING,
        placeholder: 'person@example.com, another@example.com',
        hint: 'Separate email addresses with commas, spaces, or new lines.',
        required: true,
        validators: [this.emailAddressesTextValidator()],
        errorMessages: {
          invalidEmailList: 'Email Addresses must contain valid email addresses separated by commas, spaces, or new lines.',
        },
        readOnly,
        invisible: form => form.getRawValue().memberType !== GroupMemberInterface.EmailAddress,
      },
    ];
  }

  private async loadMemberOptions(
    memberType: GroupMemberInterface,
    selectedMembers: string[] = [],
  ): Promise<string[]> {
    if (memberType === GroupMemberInterface.EmailAddress) {
      return [];
    }

    try {
      const response = await this.groupsService.getItemsByType(memberType, {
        pagination: {
          page: 1,
          limit: 1000,
          selectedMembers,
        },
        filter: { filters: [] },
      });
      if (response.success === false) {
        throw new Error(response.error);
      }
      const values = response.data.members.map(member => this.formatMemberOption(memberType, member));
      if (memberType === GroupMemberInterface.ExistingUser) {
        return values.sort((a, b) => a.split(':')[0].localeCompare(b.split(':')[0]));
      }
      return values;
    } catch (error) {
      console.error(error);
      this.snackBar.open('Unable to load group member options. Try again in a few minutes.', '', {
        duration: 2000,
      });
      return [];
    }
  }

  private formatMemberOption(memberType: GroupMemberInterface, member: { id: string; name: string; email?: string; emailAddress?: string }): string {
    const email = member.email || member.emailAddress;
    const label = memberType === GroupMemberInterface.ExistingUser && email
      ? `${member.name} (${email})`
      : member.name;
    return `${label}:${member.id}`;
  }

  private toGroupDialogData(group: GroupInterface): Record<string, any> {
    return {
      name: group.name,
      description: group.description,
      memberType: group.memberType,
      memberIds: group.memberType === GroupMemberInterface.EmailAddress ? [] : group.members || [],
      emailAddressesText: group.memberType === GroupMemberInterface.EmailAddress ? (group.members || []).join(', ') : '',
    };
  }

  private toCreatePayload(result: Record<string, any>): Record<string, any> {
    const payload = {
      name: result.name,
      description: result.description,
    };
    const idsKey = this.getIdsKey(result.memberType);
    if (idsKey) {
      return { ...payload, [idsKey]: result.memberIds || [] };
    }
    return { ...payload, emailAddresses: this.parseEmailAddresses(result.emailAddressesText) };
  }

  private toUpdatePayload(result: Record<string, any>): Record<string, any> {
    const memberType = result.memberType as GroupMemberInterface;
    const members = memberType === GroupMemberInterface.EmailAddress
      ? this.parseEmailAddresses(result.emailAddressesText)
      : result.memberIds || [];
    return {
      name: result.name,
      description: result.description,
      memberType,
      members,
    };
  }

  private getIdsKey(memberType: GroupMemberInterface): string | null {
    return {
      [GroupMemberInterface.Team]: 'teamIds',
      [GroupMemberInterface.Company]: 'companyIds',
      [GroupMemberInterface.Brand]: 'brandIds',
      [GroupMemberInterface.ExistingUser]: 'existingUsersIds',
      [GroupMemberInterface.EmailAddress]: null,
    }[memberType];
  }

  private parseEmailAddresses(value: string): string[] {
    return `${value || ''}`
      .split(/[\s,;]+/)
      .map(email => email.trim())
      .filter(email => !!email);
  }

  private emailAddressesTextValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const emails = this.parseEmailAddresses(control.value || '');
      if (!emails.length) {
        return { required: true };
      }

      const hasInvalidEmail = emails.some(email => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
      return hasInvalidEmail ? { invalidEmailList: true } : null;
    };
  }
}
